import { zoom, ZoomBehavior, zoomIdentity, ZoomTransform } from 'd3-zoom';
import React, {
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    computeZoomedTransform,
    getBoundedTransform,
    isTransfromScaleBelowMin,
    processZoomEvents,
} from './chartZoom';
import { ZoomControl } from 'utils/components/Zoom/chartZoom';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyScale = any;

export interface CommonZoomContextState {
    zoomObj: ZoomBehavior<Element, unknown>;
    zoomIn: () => void;
    zoomOut: () => void;
    transform: ZoomTransform;
}

export interface HorizontalZoomContextState extends CommonZoomContextState {
    startX: number;
    endX: number;
    zoomedXScale: AnyScale;
    widthToInterval: (fromWidth: number) => number;
    intervalToWidth: (fromInterval: number) => number;
}

export interface VerticalZoomContextState extends CommonZoomContextState {
    startY: number;
    endY: number;
    zoomedYScale: AnyScale;
}

export type MultiZoomContextState = HorizontalZoomContextState &
    VerticalZoomContextState;

export type ZoomContextState =
    | MultiZoomContextState
    | VerticalZoomContextState
    | HorizontalZoomContextState;

export const ZoomContext = React.createContext<ZoomContextState | undefined>(
    undefined
);

interface ZoomContextProviderProps {
    allowZoom?: boolean;
    horizontal?: {
        boundaries: number[];
        dataXScale: AnyScale;
        plotAreaWidth: number;
    };
    vertical?: {
        boundaries: number[];
        dataYScale: AnyScale;
        plotAreaHeight: number;
    };
    resetZoomOnBoundaryChange?: boolean;
    onZoomScaleBoundary?: () => void;
    onZoomIn?: (c: ZoomControl) => void;
    onZoomOut?: (c: ZoomControl) => void;
}

export function ZoomContextProvider(
    props: PropsWithChildren<ZoomContextProviderProps>
) {
    const {
        allowZoom = true,
        vertical,
        horizontal,
        children,
        onZoomScaleBoundary,
        onZoomIn,
        onZoomOut,
        resetZoomOnBoundaryChange = true,
    } = props;

    const ret = {} as ZoomContextState;

    // This ZoomTransform is the master/reference for all child TimeSeriesCharts
    // and controls the zoom level and scroll position for each.
    // const [transform, setTransform] = useState<ZoomTransform>(zoomIdentity);
    const [transform, setTransform] = useState<ZoomTransform>(zoomIdentity);

    const zoomedXScale = useMemo(
        () => (horizontal ? horizontal.dataXScale.copy() : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [horizontal?.dataXScale]
    );
    const zoomedYScale = useMemo(
        () => (vertical ? vertical.dataYScale.copy() : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [vertical?.dataYScale]
    );

    useEffect(() => {
        if (resetZoomOnBoundaryChange) {
            setTransform(zoomIdentity);
        }
    }, [
        vertical?.boundaries,
        horizontal?.boundaries,
        resetZoomOnBoundaryChange,
    ]);

    const xDomain = useMemo(
        () =>
            horizontal
                ? transform.rescaleX(horizontal.dataXScale).domain()
                : [0, 1],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [transform, horizontal, horizontal?.dataXScale]
    );

    const widthToInterval = useCallback(
        (fromWidth: number) => {
            const xWidth = xDomain[1] - xDomain[0];
            return (xWidth / (horizontal?.plotAreaWidth ?? 1)) * fromWidth;
        },
        [xDomain, horizontal?.plotAreaWidth]
    );
    const intervalToWidth = useCallback(
        (fromInterval: number) => {
            const xWidth = xDomain[1] - xDomain[0];
            return (fromInterval / xWidth) * (horizontal?.plotAreaWidth ?? 1);
        },
        [xDomain, horizontal?.plotAreaWidth]
    );

    zoomedXScale.domain(xDomain);

    if (horizontal) {
        // chart x-scale's domain is updated based on ZoomTransform.

        const horizontalState = ret as HorizontalZoomContextState;
        horizontalState.startX = xDomain[0];
        horizontalState.endX = xDomain[1];
        horizontalState.zoomedXScale = zoomedXScale;
        horizontalState.widthToInterval = widthToInterval;
        horizontalState.intervalToWidth = intervalToWidth;
    }

    if (vertical) {
        // chart x-scale's domain is updated based on ZoomTransform.
        const yDomain = transform.rescaleY(vertical.dataYScale).domain();
        zoomedYScale.domain(yDomain);

        const verticalState = ret as VerticalZoomContextState;
        verticalState.startY = yDomain[0];
        verticalState.endY = yDomain[1];
        verticalState.zoomedYScale = zoomedYScale;
    }

    // function to ensure that zoom transform stays within chart bounds
    const setTransformWrapper = useCallback(
        (t: ZoomTransform) => {
            if (allowZoom) {
                if (onZoomScaleBoundary && isTransfromScaleBelowMin(t)) {
                    onZoomScaleBoundary();
                }

                setTransform(
                    getBoundedTransform(
                        t,
                        horizontal?.plotAreaWidth,
                        vertical?.plotAreaHeight
                    )
                );
            }
        },
        [
            horizontal?.plotAreaWidth,
            vertical?.plotAreaHeight,
            setTransform,
            allowZoom,
            onZoomScaleBoundary,
        ]
    );

    const tempTransformRef = useRef<ZoomTransform>(zoomIdentity);
    // d3-zoom obj - handles mouse-driven zoom interactions
    const zoomObj = useMemo(
        () =>
            zoom()
                .on('start', (event) => {
                    tempTransformRef.current = event.transform;
                })
                .on('zoom', (event) => {
                    setTransformWrapper(event.transform);
                })
                .on('end', (event) => {
                    setTransformWrapper(
                        processZoomEvents(
                            tempTransformRef.current,
                            event.transform,
                            onZoomIn,
                            onZoomOut
                        )
                    );
                }),
        [setTransformWrapper, onZoomIn, onZoomOut]
    );

    // applyZoom - handles button-related zoom interactions
    const applyZoom = useCallback(
        (scale: number) => {
            setTransformWrapper(
                processZoomEvents(
                    transform,
                    computeZoomedTransform(
                        transform,
                        scale,
                        horizontal?.plotAreaWidth,
                        vertical?.plotAreaHeight
                    ),
                    onZoomIn,
                    onZoomOut
                )
            );
        },
        [
            horizontal?.plotAreaWidth,
            vertical?.plotAreaHeight,
            transform,
            setTransformWrapper,
            onZoomIn,
            onZoomOut,
        ]
    );

    const zoomIn = () => applyZoom(2);
    const zoomOut = () => applyZoom(0.5);

    ret.zoomObj = zoomObj;
    ret.zoomIn = zoomIn;
    ret.zoomOut = zoomOut;
    ret.transform = transform;

    return <ZoomContext.Provider value={ret}>{children}</ZoomContext.Provider>;
}

export function useZoomContext() {
    return useContext(ZoomContext)!;
}

export function useHorizontalZoomContext() {
    return useZoomContext() as HorizontalZoomContextState;
}

export function useMultipleZoomContext() {
    return useZoomContext() as MultiZoomContextState;
}
